import { Component, DestroyRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AntiForgeryService } from 'src/app/auth/auth-antiforgery.service';
import { NavRoutes } from 'src/app/core/constants/nav.routes';
import { NotificationMessage, NotificationType, PasswordVerificationDto } from 'src/openapi-gen/models';
import { AccountService } from 'src/openapi-gen/services';
import { ProgressBarComponent } from 'src/app/shared/components/progress-bar/progress-bar.component';
import { ErrorResponseHandler } from 'src/app/shared/services/error-response-handler';
import { ErrorPageQueryParameters } from 'src/app/shared/pages/error-page/error-page.component';
import { map } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { FlexModule } from '@angular/flex-layout/flex';
import { ToastComponent } from "../../../shared/components/toasts/toast.component";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { passwordMatchValidator } from 'src/app/shared/validators/confirm.password.validator';
import { Timeout } from 'src/app/shared/helpers/Timeout';
import { RegexExpressions } from '../../constants/regex';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { BaseComponent } from 'src/app/shared/components/base/base.component';
import { ProgressBarModule } from 'primeng/progressbar';
import { MessageService } from 'primeng/api';
import { MatIconModule } from '@angular/material/icon';

@Component({
    selector: 'app-verify-account',
    templateUrl: "verify-account.component.html",
    standalone: true,
    providers: [MessageService],
    imports: [
        NgIf,
        FlexModule,
        MatCardModule,
        ProgressBarComponent,
        MatButtonModule,
        ToastComponent,
        MatFormFieldModule,
        MatInputModule,
        ReactiveFormsModule,
        ProgressBarModule,
        MatIconModule
    ]
})
export class VerifyAccountComponent extends BaseComponent implements OnInit {
    private destroyRef = inject(DestroyRef);

    @Input() code?: string; // Injected from query parameter. Name must match 1:1 in the query parameter.

    hideConfirmation = true;

    passwordForm: FormGroup;
    readonly passwordValidationMessage: string = 'Must have 1 upper & 1 lower case letter, 1 digit, and 1 special character "! @ # $ & *"';

    form_validation_messages = {
        'password': [
            { type: 'required', message: 'Please enter a password' },
            { type: 'maxlength', message: "Password is too long"},
            { type: 'minlength', message: "Password is too short, must be minimum of 10 characters"},
            { type: 'pattern', message: this.passwordValidationMessage }
        ],
        'confirmPassword':[
            { type: 'required', message: 'Please confirm password' },
            { type: 'passwordMismatch', message: 'Passwords must match' }
        ]
    };

    @ViewChild(ToastComponent, { static: true }) toast: ToastComponent;
    @ViewChild(ProgressBarComponent) progressBar: ProgressBarComponent;
    constructor(
        private router: Router,
        private errorResponseHandler: ErrorResponseHandler,
        private accountService: AccountService,
        private antiForgeryService: AntiForgeryService
    ) { 
        super();
    }

    ngOnInit() {
        this.createForm();
    }

    private createForm() {
        this.passwordForm = new FormGroup({
            password: new FormControl(null, [
                Validators.required,
                Validators.minLength(10), 
                Validators.maxLength(30),
                Validators.pattern(RegexExpressions.Password)
            ]),
            confirmPassword: new FormControl(null, [
                Validators.required, 
                (control) => passwordMatchValidator('password', control)
            ])
        });
    }

    toggleHidePassword() {
        this.hideConfirmation = !this.hideConfirmation;
    }

    verifyAccount() {
        let verification = {
            newPassword: this.passwordForm.controls.password.value,
            verificationCode: this.code.formatVerificationCodeFromUrl()
        } as PasswordVerificationDto;

        this.hideConfirmation = true;
        this.passwordForm.disable();
        this.progressBar.show();
        
        this.antiForgeryService.applyAntiForgery(
            this.accountService.apiAccountVerifyAccountPost({ body: verification }).pipe(map(_ => _), takeUntilDestroyed(this.destroyRef)), 
            {
                next: (_) => {
                    this.toast.showToast({action: NotificationType.Success, message: "Account registration success. You can now login!"} as NotificationMessage);
                    Timeout.delayActionShort(() => {
                        this.progressBar.hide();
                        this.router.navigate([NavRoutes.Root], { replaceUrl: true });
                    });
                },
                error: (err) => {
                    this.passwordForm.enable();
                    this.progressBar.hide();

                    let queryParams = {
                        headerText: "Account Verification Expired",
                        subText: "Please contact an administrator if you are unable to log in or try to reset your password!",
                        materialIconName: "warning",
                        materialIconColorClass: "color-warn",
                        onClickButtonName: "Home",
                        onClickNavigationRoute: NavRoutes.Root,
                        replaceUrl: true
                    } as ErrorPageQueryParameters;

                    this.errorResponseHandler.handle_verification_code(err.status, queryParams);
                }
            }
        );
    }

    redirectToLogIn() {
        this.router.navigate([NavRoutes.Login], { replaceUrl: true});
    }
}
